import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import type { PurchaseResponse } from '@ozon-ai/procurement/contracts';
import { PURCHASE_STATUS_LABELS } from '@ozon-ai/procurement/contracts';
import { StatusBadge } from '../ui/StatusBadge';
import { useThemePreference } from '../context/ThemePreferenceContext';
import { buildPurchaseGridTheme } from '../theme/agGridTheme';
import {
  PurchaseListActionsCell,
  type PurchaseListActionsCellParams,
} from './PurchaseListActionsCell';
import '../styles/ag-grid.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 48;

function formatPurchaseDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('ru-RU');
}

function formatRub(value: number): string {
  return `${value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
}

function lineCount(purchase: PurchaseResponse): number {
  if (purchase.lines?.length) return purchase.lines.length;
  try {
    const parsed = JSON.parse(purchase.linesJson) as unknown[];
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

interface PurchasesListGridProps {
  purchases: PurchaseResponse[];
  onEdit: (purchase: PurchaseResponse) => void;
  onView: (purchase: PurchaseResponse) => void;
  onDeactivate: (purchase: PurchaseResponse) => void;
}

export function PurchasesListGrid({
  purchases,
  onEdit,
  onView,
  onDeactivate,
}: PurchasesListGridProps) {
  const { resolvedColorScheme } = useThemePreference();
  const gridRef = useRef<AgGridReact<PurchaseResponse>>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const isDarkTheme = resolvedColorScheme === 'dark';
  const gridTheme = useMemo(() => buildPurchaseGridTheme(isDarkTheme), [isDarkTheme]);

  const columnDefs = useMemo<ColDef<PurchaseResponse>[]>(
    () => [
      {
        colId: 'effectiveFrom',
        headerName: 'Дата закупки',
        flex: 1.2,
        minWidth: 140,
        sortable: true,
        valueGetter: (params) => params.data?.effectiveFrom ?? '',
        comparator: (left, right) => String(left).localeCompare(String(right)),
        cellRenderer: (params: ICellRendererParams<PurchaseResponse>) => {
          const purchase = params.data;
          if (!purchase) return null;
          const title = purchase.title || `Закупка ${purchase.id.slice(0, 6)}`;
          return (
            <div className="flex min-w-0 flex-col justify-center py-1 leading-tight">
              <span className="truncate text-sm">{formatPurchaseDate(purchase.effectiveFrom)}</span>
              <span className="truncate text-xs text-muted-foreground">{title}</span>
            </div>
          );
        },
      },
      {
        colId: 'positions',
        headerName: 'Позиций',
        flex: 0.6,
        minWidth: 90,
        sortable: true,
        type: 'numericColumn',
        valueGetter: (params) => (params.data ? lineCount(params.data) : 0),
      },
      {
        field: 'totalWithDeliveryRub',
        headerName: 'Сумма',
        flex: 0.9,
        minWidth: 110,
        sortable: true,
        type: 'numericColumn',
        valueFormatter: (params: ValueFormatterParams<PurchaseResponse, number>) =>
          params.value != null ? formatRub(params.value) : '—',
      },
      {
        colId: 'status',
        headerName: 'Статус',
        flex: 0.55,
        minWidth: 84,
        sortable: true,
        valueGetter: (params) => params.data?.status ?? '',
        cellRenderer: (params: ICellRendererParams<PurchaseResponse>) => {
          const purchase = params.data;
          if (!purchase) return null;
          const label = PURCHASE_STATUS_LABELS[purchase.status] || purchase.status;
          return (
            <div className="flex h-full items-center">
              <StatusBadge status={purchase.status}>{label}</StatusBadge>
            </div>
          );
        },
      },
      {
        colId: 'actions',
        headerName: 'Действия',
        width: 112,
        maxWidth: 112,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellRenderer: PurchaseListActionsCell,
        cellRendererParams: {
          onEdit,
          onView,
          onDeactivate,
        } satisfies Partial<PurchaseListActionsCellParams>,
      },
    ],
    [onDeactivate, onEdit, onView]
  );

  const defaultColDef = useMemo<ColDef<PurchaseResponse>>(
    () => ({
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const onGridReady = useCallback((event: GridReadyEvent<PurchaseResponse>) => {
    event.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new ResizeObserver(() => {
      gridRef.current?.api?.sizeColumnsToFit();
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={hostRef} className="ag-grid-host ag-grid-host--fill w-full min-h-0">
      <AgGridReact<PurchaseResponse>
        ref={gridRef}
        theme={gridTheme}
        rowData={purchases}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        popupParent={document.body}
        domLayout="normal"
        getRowId={(params) => params.data.id}
        onGridReady={onGridReady}
        suppressCellFocus
        overlayNoRowsTemplate="Закупок нет"
        rowClassRules={{
          'opacity-60': (params) => Boolean(params.data?.inactive),
        }}
      />
    </div>
  );
}
